import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Bank, ArrowClockwise, MagnifyingGlass, PlayCircle, CheckCircle, WarningCircle,
  Question, X, ArrowCounterClockwise, Buildings, Storefront, GitMerge, PencilSimple,
} from "@phosphor-icons/react";

const fmt = (n) => (n ?? 0).toLocaleString("fr-FR");

const VERDICT = {
  certain: { label: "Certain", color: "#22C55E", icon: CheckCircle },
  probable: { label: "Probable", color: "#F5A623", icon: Question },
  ambiguous: { label: "À vérifier", color: "#F97316", icon: WarningCircle },
  not_found: { label: "Introuvable", color: "#8A8A93", icon: X },
  error: { label: "Erreur", color: "#EF4444", icon: X },
};

function Stat({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-surface border border-border rounded-lg px-4 py-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted text-xs mb-1">
        {Icon && <Icon size={13} />}{label}
      </div>
      <div className="text-2xl font-semibold text-fg">{value}</div>
      {sub && <div className="text-xs text-muted mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AdminSirene() {
  const [config, setConfig] = useState(null);
  const [audit, setAudit] = useState(null);
  const [state, setState] = useState(null);
  const [dryrun, setDryrun] = useState(null);
  const [dryLimit, setDryLimit] = useState(20);
  const [busy, setBusy] = useState("");
  const [queue, setQueue] = useState([]);
  const [sirenInput, setSirenInput] = useState({});
  const [p2count, setP2count] = useState(null);

  const loadState = useCallback(async () => {
    try { const { data } = await api.get("/admin/sirene/state"); setState(data); } catch { /* */ }
  }, []);
  const loadQueue = useCallback(async () => {
    try { const { data } = await api.get("/admin/sirene/match-queue?status=pending"); setQueue(data.items); } catch { /* */ }
  }, []);

  useEffect(() => {
    api.get("/admin/sirene/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ configured: false }));
    api.get("/admin/sirene/audit").then(({ data }) => setAudit(data)).catch(() => {});
    api.get("/admin/sirene/phase2/count").then(({ data }) => setP2count(data)).catch(() => {});
    loadState(); loadQueue();
  }, [loadState, loadQueue]);

  // Poll state while any run is active
  useEffect(() => {
    const running = ["phase1", "phase2_ul", "phase2_etab", "name_fix"].some((k) => state?.[k]?.status === "running");
    if (!running) return;
    const t = setInterval(loadState, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.phase1?.status, state?.phase2_ul?.status, state?.phase2_etab?.status, state?.name_fix?.status, loadState]);

  const runDryRun = async () => {
    setBusy("dry"); setDryrun(null);
    try {
      const { data } = await api.post("/admin/sirene/phase1/dry-run", { limit: Number(dryLimit) });
      setDryrun(data);
      toast.success(`Dry-run terminé sur ${data.scanned} entreprises`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const runApply = async () => {
    if (!window.confirm("Lancer la normalisation Phase 1 sur TOUTES les entreprises sans SIREN ?\n\n- Les rapprochements CERTAINS sont appliqués (données commerciales préservées)\n- Les cas ambigus partent en file de validation\n- Aucune suppression. Rollback possible.\n\nLe traitement s'exécute en arrière-plan (API INSEE limitée ~30 req/min).")) return;
    setBusy("apply");
    try {
      await api.post("/admin/sirene/phase1/apply", {});
      toast.success("Normalisation lancée en arrière-plan");
      setTimeout(loadState, 1500);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const rollback = async () => {
    const rid = state?.phase1?.run_id;
    if (!rid) return toast.error("Aucun run à annuler");
    if (!window.confirm(`Annuler le run ${rid} ? Les champs administratifs INSEE seront retirés (données commerciales intactes).`)) return;
    setBusy("rollback");
    try {
      const { data } = await api.post("/admin/sirene/rollback", { run_id: rid });
      toast.success(`Rollback effectué : ${data.restored} entreprise(s) restaurée(s)`);
      loadState();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const resolve = async (item, action, siren) => {
    try {
      await api.post(`/admin/sirene/match-queue/${item.id}/resolve`, action === "reject" ? { action: "reject" } : { action: "assign", siren });
      toast.success(action === "reject" ? "Rejeté" : "Entreprise rattachée au SIREN");
      loadQueue(); loadState();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const importUL = async () => {
    if (!window.confirm(`Importer les ~${fmt(p2count?.target_total)} unités légales actives 62.02A (≥1 salarié) ?\n\nUPSERT sur SIREN (aucun doublon). Tâche de fond reprenable (API INSEE ~30 req/min).`)) return;
    setBusy("p2ul");
    try { await api.post("/admin/sirene/phase2/import-ul", {}); toast.success("Import national lancé en arrière-plan"); setTimeout(loadState, 1500); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const fillEtab = async () => {
    if (!window.confirm("Récupérer les établissements (SIRET) des entreprises importées ? Tâche de fond longue (API rate-limitée).")) return;
    setBusy("p2etab");
    try { await api.post("/admin/sirene/phase2/fill-establishments", {}); toast.success("Récupération des établissements lancée"); setTimeout(loadState, 1500); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const dailyNow = async () => {
    setBusy("daily");
    try { const { data } = await api.post("/admin/sirene/daily-sync", {}); toast.success(data.ok ? `Synchro : ${fmt(data.updated)} mise(s) à jour` : "Synchro : rien à mettre à jour"); loadState(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const fixNames = async () => {
    const n = state?.companies_name_is_siren || 0;
    if (!n) return toast.info("Aucun nom à corriger");
    if (!window.confirm(`Corriger le nom de ${fmt(n)} entreprise(s) affichant leur SIREN ?\n\nInterrogation de l'API INSEE par lots de 50 (1s entre lots). Le nom est mis à jour au fil de l'eau, en arrière-plan.`)) return;
    setBusy("namefix");
    try { await api.post("/admin/sirene/fix-names", {}); toast.success("Correction des noms lancée en arrière-plan"); setTimeout(loadState, 1200); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const st = state?.phase1 || {};
  const tally = st.tally || {};
  const nf = state?.name_fix || {};

  return (
    <div className="space-y-6" data-testid="admin-sirene">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg flex items-center gap-2"><Bank size={22} className="text-accent" /> Référentiel SIRENE</h1>
          <p className="text-sm text-muted mt-1">Normalisation du référentiel entreprises françaises via l'API INSEE Sirene 3.11 (source de vérité administrative).</p>
        </div>
        <button onClick={() => { loadState(); loadQueue(); }} className="flex items-center gap-1.5 text-xs border border-border rounded-md px-3 py-2 text-muted hover:text-fg" data-testid="sirene-refresh">
          <ArrowClockwise size={14} /> Actualiser
        </button>
      </div>

      {config && !config.configured && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-300" data-testid="sirene-not-configured">
          <WarningCircle size={16} className="inline mr-1.5" /> Clé INSEE_API_KEY non configurée côté serveur.
        </div>
      )}

      {/* Etat de synchronisation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Entreprises avec SIREN" value={fmt(state?.companies_with_siren)} sub={audit ? `sur ${fmt(audit.companies.total)}` : ""} icon={Buildings} testid="stat-with-siren" />
        <Stat label="Établissements avec SIRET" value={fmt(state?.establishments_with_siret)} sub={audit ? `sur ${fmt(audit.establishments.total)}` : ""} icon={Storefront} testid="stat-with-siret" />
        <Stat label="File de validation" value={fmt(state?.queue_pending)} sub="cas ambigus" icon={WarningCircle} testid="stat-queue" />
        <Stat label="Doublons fusionnés" value={fmt(state?.merged)} sub="redirection conservée" icon={GitMerge} testid="stat-merged" />
      </div>

      {/* Audit Phase 0 */}
      {audit && (
        <div className="bg-surface border border-border rounded-lg p-4" data-testid="sirene-audit-card">
          <h2 className="text-sm font-semibold text-fg mb-3">Audit du référentiel (Phase 0)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            <div><span className="text-muted">Entreprises : </span><span className="text-fg font-medium">{fmt(audit.companies.total)}</span> <span className="text-muted">({fmt(audit.companies.without_siren)} sans SIREN)</span></div>
            <div><span className="text-muted">Établissements : </span><span className="text-fg font-medium">{fmt(audit.establishments.total)}</span> <span className="text-muted">({fmt(audit.establishments.headquarters)} sièges)</span></div>
            <div><span className="text-muted">Doublons domaine : </span><span className="text-fg font-medium">{fmt(audit.duplicates.by_domain_groups)} groupes</span> <span className="text-muted">(+{fmt(audit.duplicates.by_domain_extra)})</span></div>
            <div><span className="text-muted">Doublons nom : </span><span className="text-fg font-medium">{fmt(audit.duplicates.by_name_groups)} groupes</span></div>
            <div><span className="text-muted">Signaux : </span><span className="text-fg font-medium">{fmt(audit.business_relations.signals)}</span></div>
            <div><span className="text-muted">Contacts : </span><span className="text-fg font-medium">{fmt(audit.business_relations.contacts)}</span></div>
            <div><span className="text-muted">Patterns email : </span><span className="text-fg font-medium">{fmt(audit.business_relations.email_patterns)}</span></div>
            <div><span className="text-muted">Sans domaine : </span><span className="text-fg font-medium">{fmt(audit.companies.without_domain)}</span></div>
          </div>
          {audit.duplicates.top_domain_dups?.length > 0 && (
            <div className="mt-3 text-xs text-muted">Top doublons domaine : {audit.duplicates.top_domain_dups.slice(0, 6).join(" · ")}</div>
          )}
        </div>
      )}

      {/* Actions Phase 1 */}
      <div className="bg-surface border border-border rounded-lg p-4 space-y-3" data-testid="sirene-phase1">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-fg">Phase 1 — Normalisation des entreprises existantes</h2>
          {st.status && (
            <span className="text-xs px-2 py-0.5 rounded-full border border-accent/30 text-accent bg-accent/10">
              {st.status === "running" ? `En cours… ${fmt(st.processed)} traitées` : st.status}
            </span>
          )}
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs text-muted block mb-1">Taille de l'échantillon (dry-run)</label>
            <Input type="number" min={1} max={60} value={dryLimit} onChange={(e) => setDryLimit(e.target.value)} className="h-9 w-28 bg-surface-2 border-border" data-testid="sirene-dry-limit" />
          </div>
          <button onClick={runDryRun} disabled={busy === "dry"} data-testid="sirene-dryrun-btn"
            className="flex items-center gap-1.5 text-xs font-semibold border border-accent/40 text-accent px-3 py-2 rounded-md hover-lift disabled:opacity-50">
            <MagnifyingGlass size={14} /> {busy === "dry" ? "Analyse…" : "Lancer un dry-run"}
          </button>
          <button onClick={runApply} disabled={busy === "apply" || st.status === "running"} data-testid="sirene-apply-btn"
            className="flex items-center gap-1.5 text-xs font-semibold bg-accent text-black px-3 py-2 rounded-md hover-lift disabled:opacity-50">
            <PlayCircle size={14} weight="fill" /> Lancer la normalisation (réelle)
          </button>
          {st.run_id && (
            <button onClick={rollback} disabled={busy === "rollback"} data-testid="sirene-rollback-btn"
              className="flex items-center gap-1.5 text-xs border border-border text-muted hover:text-red-300 px-3 py-2 rounded-md disabled:opacity-50">
              <ArrowCounterClockwise size={14} /> Rollback dernier run
            </button>
          )}
        </div>
        {(tally.applied != null && st.status) && (
          <div className="flex flex-wrap gap-3 text-xs text-muted pt-1">
            <span className="text-[#22C55E]">Appliqués : {fmt(tally.applied)}</span>
            <span className="text-[#F97316]">En file : {fmt(tally.queued)}</span>
            <span>Fusionnés : {fmt(tally.merged)}</span>
            <span>Introuvables : {fmt(tally.not_found)}</span>
          </div>
        )}
      </div>

      {/* Résultats dry-run */}
      {dryrun && (
        <div className="bg-surface border border-border rounded-lg p-4" data-testid="sirene-dryrun-results">
          <div className="flex items-center gap-3 mb-3 text-xs">
            <span className="text-fg font-medium">Dry-run : {fmt(dryrun.scanned)} entreprises</span>
            {Object.entries(dryrun.tally).filter(([k]) => VERDICT[k]).map(([k, v]) => (
              <span key={k} style={{ color: VERDICT[k].color }}>{VERDICT[k].label} : {v}</span>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-muted text-xs border-b border-border">
                <th className="text-left py-2">Entreprise InterKO</th><th className="text-left">Verdict</th>
                <th className="text-left">Correspondance Sirene</th><th className="text-left">SIREN</th>
                <th className="text-left">APE</th><th className="text-left">sim</th><th className="text-left">total</th></tr></thead>
              <tbody>
                {dryrun.samples.map((s, i) => {
                  const vm = VERDICT[s.verdict] || VERDICT.error; const Vi = vm.icon;
                  return (
                    <tr key={i} className="border-b border-border/40" data-testid={`dryrun-row-${i}`}>
                      <td className="py-2 text-fg font-medium truncate max-w-[220px]">{s.company}</td>
                      <td><span className="inline-flex items-center gap-1 text-xs" style={{ color: vm.color }}><Vi size={12} /> {vm.label}</span></td>
                      <td className="text-muted truncate max-w-[200px]">{s.best || "—"}</td>
                      <td className="text-muted">{s.siren || "—"}</td>
                      <td className="text-muted">{s.ape || "—"}</td>
                      <td className="text-muted">{s.name_sim ?? "—"}</td>
                      <td className="text-muted">{s.total ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Phase 2 — Import national */}
      <div className="bg-surface border border-border rounded-lg p-4 space-y-3" data-testid="sirene-phase2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-fg">Phase 2 — Import national 62.02A (employeurs ≥1 salarié)</h2>
          <span className="text-xs text-muted">Cible : <span className="text-accent font-medium">{fmt(p2count?.target_total)}</span> unités légales</span>
        </div>
        <p className="text-xs text-muted">Import via API INSEE (curseur, reprenable, UPSERT sur SIREN — aucun doublon). Les établissements et la synchro quotidienne complètent le référentiel.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={importUL} disabled={busy === "p2ul" || state?.phase2_ul?.status === "running"} data-testid="sirene-import-ul-btn"
            className="flex items-center gap-1.5 text-xs font-semibold bg-accent text-black px-3 py-2 rounded-md hover-lift disabled:opacity-50">
            <Buildings size={14} weight="fill" /> Importer les unités légales
          </button>
          <button onClick={fillEtab} disabled={busy === "p2etab" || state?.phase2_etab?.status === "running"} data-testid="sirene-fill-etab-btn"
            className="flex items-center gap-1.5 text-xs font-semibold border border-accent/40 text-accent px-3 py-2 rounded-md hover-lift disabled:opacity-50">
            <Storefront size={14} /> Récupérer les établissements
          </button>
          <button onClick={dailyNow} disabled={busy === "daily"} data-testid="sirene-daily-btn"
            className="flex items-center gap-1.5 text-xs border border-border text-muted hover:text-fg px-3 py-2 rounded-md disabled:opacity-50">
            <ArrowClockwise size={14} /> Synchro incrémentale
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          {[["phase2_ul", "Unités légales", "created"], ["phase2_etab", "Établissements", "establishments"], ["daily", "Synchro quotidienne", null]].map(([k, label, metric]) => {
            const s = state?.[k] || {};
            return (
              <div key={k} className="border border-border/60 rounded-lg px-3 py-2" data-testid={`p2-${k}`}>
                <div className="text-muted mb-1">{label} {s.status && <span className="text-accent">· {s.status === "running" ? "en cours…" : s.status}</span>}</div>
                {k === "daily" ? (
                  <div className="text-fg">{s.last_sync_date ? `Dernière : ${s.last_sync_date} (${fmt(s.last_updated)} maj)` : "Programmée quotidiennement (04:30 UTC)"}</div>
                ) : (
                  <div className="text-fg">{fmt(s.processed)} traités{metric && s[metric] != null ? ` · ${fmt(s[metric])} ${metric === "created" ? "créées" : "établissements"}` : ""}{s.target_total ? ` / ${fmt(s.target_total)}` : ""}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Correction des noms manquants */}
      <div className="bg-surface border border-border rounded-lg p-4 space-y-3" data-testid="sirene-namefix">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-fg">Correction des noms (nom affiché = SIREN)</h2>
          {nf.status && (
            <span className="text-xs px-2 py-0.5 rounded-full border border-accent/30 text-accent bg-accent/10" data-testid="namefix-status">
              {nf.status === "running" ? `En cours… ${fmt(nf.processed)}/${fmt(nf.total)}` : nf.status}
            </span>
          )}
        </div>
        <p className="text-xs text-muted">
          <span className="text-fg font-medium">{fmt(state?.companies_name_is_siren)}</span> entreprise(s) affichent leur SIREN comme nom (souvent des entrepreneurs individuels sans dénomination sociale). La correction récupère la dénomination — ou le nom + prénom — via l'API INSEE Sirene, par lots de 50 (1s entre chaque lot), et met à jour le nom au fil de l'eau.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={fixNames} disabled={busy === "namefix" || nf.status === "running" || !state?.companies_name_is_siren} data-testid="sirene-fix-names-btn"
            className="flex items-center gap-1.5 text-xs font-semibold bg-accent text-black px-3 py-2 rounded-md hover-lift disabled:opacity-50">
            <PencilSimple size={14} weight="fill" /> Corriger les noms ({fmt(state?.companies_name_is_siren)})
          </button>
        </div>
        {nf.total != null && nf.status && (
          <div className="space-y-2" data-testid="namefix-progress">
            <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${nf.total ? Math.round((nf.processed / nf.total) * 100) : 0}%` }} />
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-muted">
              <span>{fmt(nf.processed)} / {fmt(nf.total)} traitées</span>
              <span className="text-[#22C55E]">Corrigées : {fmt(nf.updated)}</span>
              <span>Sans nom diffusable : {fmt(nf.not_found)}</span>
            </div>
          </div>
        )}
      </div>

      {/* File de validation */}
      <div className="bg-surface border border-border rounded-lg p-4" data-testid="sirene-queue">
        <h2 className="text-sm font-semibold text-fg mb-3">File de validation ({fmt(queue.length)})</h2>
        {queue.length === 0 ? (
          <p className="text-sm text-muted">Aucun cas ambigu en attente.</p>
        ) : (
          <div className="space-y-3">
            {queue.map((it) => (
              <div key={it.id} className="border border-border/60 rounded-lg p-3" data-testid={`queue-item-${it.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="text-sm text-fg font-medium">{it.company_name} <span className="text-muted text-xs">{it.city || ""}</span>
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ color: (VERDICT[it.verdict] || {}).color, background: ((VERDICT[it.verdict] || {}).color || "#888") + "18" }}>{(VERDICT[it.verdict] || {}).label}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Input placeholder="SIREN (9 chiffres)" value={sirenInput[it.id] || ""} onChange={(e) => setSirenInput((s) => ({ ...s, [it.id]: e.target.value }))} className="h-8 w-40 bg-surface-2 border-border text-xs" data-testid={`queue-siren-input-${it.id}`} />
                    <button onClick={() => resolve(it, "assign", sirenInput[it.id])} className="text-xs bg-accent text-black px-2.5 py-1.5 rounded-md font-semibold" data-testid={`queue-assign-${it.id}`}>Rattacher</button>
                    <button onClick={() => resolve(it, "reject")} className="text-xs border border-border text-muted px-2.5 py-1.5 rounded-md" data-testid={`queue-reject-${it.id}`}>Rejeter</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(it.candidates || []).map((c, ci) => (
                    <button key={ci} onClick={() => setSirenInput((s) => ({ ...s, [it.id]: c.siren }))}
                      className="text-left text-xs border border-border/60 rounded px-2 py-1 hover:border-accent/50" data-testid={`queue-candidate-${it.id}-${ci}`}>
                      <span className="text-fg">{c.denomination}</span>
                      <span className="text-muted"> · {c.siren} · APE {c.ape}{c.ape_ok ? " ✓IT" : ""} · sim {c.name_sim}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
